import React, { useEffect } from 'react'
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Button,
  IconButton,
  CircularProgress,
  Tooltip,
  Alert
} from '@mui/material'
import { Refresh, AutoAwesome } from '@mui/icons-material'

// Auto-refresh cadence while at least one draft is still being generated.
const REFRESH_INTERVAL_MS = 5000

const STATUS_CHIP = {
  Pending: { label: 'In progress', color: 'warning' },
  Ready: { label: 'Ready', color: 'success' },
  Failed: { label: 'Failed', color: 'error' }
}

/**
 * Table of every draft ever generated for the partner.
 *
 * Uses the GET /api/products/drafts summary rows ({ job_id, status,
 * product_name, imported, imported_product_guid, ... }). "In progress" = status
 * Pending; "Imported" = approved into a product. While any row is Pending, the
 * list re-fetches on an interval so the table reflects jobs finishing.
 */
export function DraftsTable({
  partnerDrafts,
  partnerDraftsLoader,
  partnerDraftsError,
  onFetch,
  onReview
}) {
  useEffect(() => {
    onFetch()
  }, [onFetch])

  const hasInProgress = (partnerDrafts || []).some(d => d.status === 'Pending')

  // Keep the table live while work is outstanding; stop polling once settled.
  useEffect(() => {
    if (!hasInProgress) {
      return undefined
    }
    const timer = setInterval(onFetch, REFRESH_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [hasInProgress, onFetch])

  const productName = draft => draft.product_name || '—'

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <AutoAwesome sx={{ color: '#173E4B', mr: 1 }} />
        <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }}>
          Generated Drafts
        </Typography>
        <Tooltip title="Refresh">
          <IconButton
            onClick={onFetch}
            disabled={partnerDraftsLoader}
            size="small"
          >
            {partnerDraftsLoader ? <CircularProgress size={18} /> : <Refresh />}
          </IconButton>
        </Tooltip>
      </Box>

      {partnerDraftsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {partnerDraftsError}
        </Alert>
      )}

      {(!partnerDrafts || partnerDrafts.length === 0) &&
      !partnerDraftsLoader ? (
        <Typography variant="body2" color="text.secondary">
          No drafts generated yet. Use “Generate from Document” to create one.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Product</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Job</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(partnerDrafts || []).map(draft => {
              const chip = draft.imported
                ? { label: 'Imported', color: 'primary' }
                : STATUS_CHIP[draft.status] || {
                    label: draft.status,
                    color: 'default'
                  }
              const reviewable = draft.status === 'Ready'
              return (
                <TableRow key={draft.job_id} hover>
                  <TableCell>{productName(draft)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {draft.status === 'Pending' && (
                        <CircularProgress size={14} />
                      )}
                      <Chip
                        size="small"
                        label={chip.label}
                        color={chip.color}
                      />
                      {draft.status === 'Failed' && draft.error && (
                        <Tooltip title={draft.error}>
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ cursor: 'help' }}
                          >
                            details
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {draft.job_id}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {reviewable && onReview && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onReview(draft.job_id)}
                      >
                        {draft.imported ? 'View' : 'Review →'}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  )
}
